import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { APP_SHORT_NAME } from '../../constants/branding';
import './DomainManager.css';

const DomainManager = () => {
  const { user } = useAuth();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDomain, setNewDomain] = useState({
    domain_name: '',
    display_name: '',
    description: ''
  });

  useEffect(() => {
    loadDomains();
  }, []);

  const loadDomains = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get('/api/domains');
      setDomains(response.data.domains ?? []);
    } catch (err) {
      console.error('Error loading domains:', err);
      setError(err.response?.data?.message || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();

    try {
      setError('');
      setSuccess('');

      const response = await axios.post('/api/domains', newDomain);
      setDomains([...domains, response.data.domain]);
      setNewDomain({ domain_name: '', display_name: '', description: '' });
      setShowAddForm(false);
      setSuccess('Domain added successfully!');

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error adding domain:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleToggleDomain = async (domain) => {
    try {
      setError('');

      await axios.put(`/api/domains/${domain.id}`, {
        ...domain,
        is_active: !domain.is_active
      });

      await loadDomains();
      setSuccess(`Domain ${!domain.is_active ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating domain:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleTestDomain = async (domain) => {
    try {
      setError('');

      const response = await axios.post(`/api/domains/${domain.id}/test`);

      if (response.data.success) {
        setSuccess(`Domain test: ${response.data.message}`);
      } else {
        setError(response.data.message);
      }

      setTimeout(() => {
        setSuccess('');
        setError('');
      }, 5000);
    } catch (err) {
      console.error('Error testing domain:', err);
      setError(err.response?.data?.message || 'Failed to test domain');
    }
  };

  if (loading) {
    return (
      <div className="domain-manager">
        <div className="loading">Loading domains...</div>
      </div>
    );
  }

  return (
    <div className="domain-manager">
      <div className="domain-header">
        <h2>Domain Management</h2>
        <p>Manage domains that can access this {APP_SHORT_NAME} instance</p>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Add Domain Button */}
      <div className="domain-actions">
        <button
          className="btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : 'Add New Domain'}
        </button>
      </div>

      {/* Add Domain Form */}
      {showAddForm && (
        <form className="domain-form" onSubmit={handleAddDomain}>
          <div className="form-group">
            <label htmlFor="domain_name">Domain Name *</label>
            <input
              type="text"
              id="domain_name"
              value={newDomain.domain_name}
              onChange={(e) => setNewDomain({...newDomain, domain_name: e.target.value})}
              placeholder="e.g., example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_name">Display Name</label>
            <input
              type="text"
              id="display_name"
              value={newDomain.display_name}
              onChange={(e) => setNewDomain({...newDomain, display_name: e.target.value})}
              placeholder="e.g., Example Company"
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={newDomain.description}
              onChange={(e) => setNewDomain({...newDomain, description: e.target.value})}
              placeholder="Optional description"
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">Add Domain</button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowAddForm(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Domain List */}
      <div className="domain-list">
        <h3>Active Domains ({domains.length})</h3>

        {domains.length === 0 ? (
          <p className="no-domains">No domains configured yet.</p>
        ) : (
          domains.map(domain => (
            <div key={domain.id} className="domain-item">
              <div className="domain-info">
                <div className="domain-name">
                  <strong>{domain.domain_name}</strong>
                  {domain.is_primary && <span className="primary-badge">Primary</span>}
                </div>
                {domain.display_name && (
                  <div className="domain-display">{domain.display_name}</div>
                )}
                {domain.description && (
                  <div className="domain-description">{domain.description}</div>
                )}
                <div className="domain-meta">
                  Added: {new Date(domain.created_at).toLocaleDateString()}
                </div>
              </div>

              <div className="domain-actions">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={domain.is_active}
                    onChange={() => handleToggleDomain(domain)}
                  />
                  <span className="toggle-slider"></span>
                  <span className="toggle-label">
                    {domain.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>

                <button
                  className="btn-secondary btn-small"
                  onClick={() => handleTestDomain(domain)}
                  title="Test domain connectivity"
                >
                  Test
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DomainManager;
